import useControlledState from '@unifyapps/ui/hooks/useControlledState';
import { type Dispatch, type SetStateAction, useCallback, useRef } from 'react';

export interface UseAppSideNavStateProps {
  disableHoverListeners?: boolean;
  isPlatformSideNav?: boolean;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  controlledState?: { value?: string | null; onChange: (value: string | null) => void };
  setExpandedGroupId?: Dispatch<SetStateAction<string | null>>;
}

const useAppSideNavState = ({
  disableHoverListeners,
  isPlatformSideNav,
  controlledState,
  setExpandedGroupId,
  expanded: controlledExpanded,
  onExpandedChange,
}: UseAppSideNavStateProps) => {
  const isMouseOver = useRef(false);
  const [expanded, setExpandedState] = useControlledState<boolean>({
    defaultValue: false,
    controlledValue: controlledExpanded,
    onChange: onExpandedChange,
  });
  const isExpanded = expanded ?? false;

  const setExpanded = useCallback(
    (nextExpanded: SetStateAction<boolean>) => {
      const resolvedExpanded =
        typeof nextExpanded === 'function' ? nextExpanded(isExpanded) : nextExpanded;

      if (!resolvedExpanded) {
        setExpandedGroupId?.(null);
      }

      setExpandedState(resolvedExpanded);
    },
    [isExpanded, setExpandedGroupId, setExpandedState],
  );

  const [activeMenuItemId, setActiveMenuItemId] = useControlledState<string | null>({
    defaultValue: null,
    controlledValue: controlledState?.value,
    onChange: controlledState?.onChange,
  });
  // expansion is intentionally driven by mouseEnter instead of mouseOver to avoid expansion when
  // browser re-evaluates pointer position on page switch which can lead to the side nav being in expanded state always when pointer is over it.
  const onMouseEnter = useCallback(() => {
    isMouseOver.current = true;
    setExpanded(true);
  }, [setExpanded]);

  const onMouseLeave = useCallback(() => {
    isMouseOver.current = false;
    setExpandedGroupId?.(null);

    if (activeMenuItemId) {
      return;
    }

    setExpanded(false);
  }, [activeMenuItemId, setExpanded, setExpandedGroupId]);

  const onChangeActiveMenuItemId = useCallback(
    (id: string | null) => {
      setActiveMenuItemId(id);

      // only the platform rail is click-toggle — closing its user menu via outside-click must NOT
      // collapse it (only the rail toggle does). every other variant (incl. no-code Expandable)
      // keeps the original auto-collapse-on-menu-close behavior.
      if (!id && !isMouseOver.current && !isPlatformSideNav) {
        setExpanded(false);
      }
    },
    [setExpanded, setActiveMenuItemId, isPlatformSideNav],
  );

  return {
    expanded: isExpanded,
    activeMenuItemId,
    setActiveMenuItemId: onChangeActiveMenuItemId,
    setExpanded,
    hoverProps: disableHoverListeners
      ? {}
      : {
          onMouseEnter,
          onMouseLeave,
        },
  };
};

export default useAppSideNavState;
